import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardSnapshotCaptureAttachmentsPatchSchema,
  boardSnapshotEnabledPatchSchema,
  boardSnapshotListSchema,
  boardSnapshotRestoreBodySchema,
  boardSnapshotRestoreResponseSchema,
  boardSnapshotRetentionPatchSchema,
  type ApiError,
  type Board,
  type BoardSnapshotList,
  type BoardSnapshotRestoreBody,
  type BoardSnapshotRestoreResponse,
} from '@molt/shared';
import { get, patch, post } from './api';
import { queryKeys } from './query-keys';

export function getBoardSnapshots(boardId: string): Promise<BoardSnapshotList> {
  return get(`/boards/${encodeURIComponent(boardId)}/snapshots`, boardSnapshotListSchema);
}

export function setBoardSnapshotsEnabled(
  boardId: string,
  enabled: boolean,
): Promise<BoardSnapshotList> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/snapshots/enabled`,
    boardSnapshotListSchema,
    boardSnapshotEnabledPatchSchema.parse({ enabled }),
  );
}

export function setBoardSnapshotRetention(
  boardId: string,
  retentionDays: number | null,
): Promise<BoardSnapshotList> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/snapshots/retention`,
    boardSnapshotListSchema,
    boardSnapshotRetentionPatchSchema.parse({ retentionDays }),
  );
}

export function setBoardSnapshotCaptureAttachments(
  boardId: string,
  captureAttachments: boolean,
): Promise<BoardSnapshotList> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/snapshots/capture-attachments`,
    boardSnapshotListSchema,
    boardSnapshotCaptureAttachmentsPatchSchema.parse({ captureAttachments }),
  );
}

export function restoreBoardSnapshot(
  boardId: string,
  snapshotId: string,
  body: BoardSnapshotRestoreBody,
): Promise<BoardSnapshotRestoreResponse> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/snapshots/${encodeURIComponent(
      snapshotId,
    )}/restore`,
    boardSnapshotRestoreResponseSchema,
    boardSnapshotRestoreBodySchema.parse(body),
  );
}

export function useBoardSnapshots(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardSnapshotList, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.snapshots(boardId),
    queryFn: () => getBoardSnapshots(boardId),
    enabled,
  });
}

export function useSetBoardSnapshotsEnabled(
  boardId: string,
): UseMutationResult<BoardSnapshotList, ApiError, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled) => setBoardSnapshotsEnabled(boardId, enabled),
    onSuccess: (snapshotList) => {
      writeBoardSnapshotList(queryClient, boardId, snapshotList);
    },
  });
}

export function useSetBoardSnapshotRetention(
  boardId: string,
): UseMutationResult<BoardSnapshotList, ApiError, number | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (retentionDays) => setBoardSnapshotRetention(boardId, retentionDays),
    onSuccess: (snapshotList) => {
      writeBoardSnapshotList(queryClient, boardId, snapshotList);
    },
  });
}

export function useSetBoardSnapshotCaptureAttachments(
  boardId: string,
): UseMutationResult<BoardSnapshotList, ApiError, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (captureAttachments) =>
      setBoardSnapshotCaptureAttachments(boardId, captureAttachments),
    onSuccess: (snapshotList) => {
      writeBoardSnapshotList(queryClient, boardId, snapshotList);
    },
  });
}

export function useRestoreBoardSnapshot(
  boardId: string,
): UseMutationResult<
  BoardSnapshotRestoreResponse,
  ApiError,
  { snapshotId: string; title?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ snapshotId, title }) =>
      restoreBoardSnapshot(boardId, snapshotId, title === undefined ? {} : { title }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.boards.detail(result.board.id), result.board);
      queryClient.setQueriesData<Board[]>(
        { queryKey: queryKeys.workspaces.boards(result.board.workspaceId, 'open') },
        (previous) => appendBoardIfMissing(previous, result.board),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
      void queryClient.invalidateQueries({
        queryKey: ['workspaces', result.board.workspaceId, 'boards'],
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.snapshots(boardId),
      });
    },
  });
}

function writeBoardSnapshotList(
  queryClient: QueryClient,
  boardId: string,
  snapshotList: BoardSnapshotList,
): void {
  queryClient.setQueryData(queryKeys.boards.snapshots(boardId), snapshotList);
}

function appendBoardIfMissing(
  previous: Board[] | undefined,
  board: Board,
): Board[] | undefined {
  if (previous === undefined) return undefined;
  if (previous.some((entry) => entry.id === board.id)) return previous;
  return [...previous, board];
}
